import {
	useGetLastLoginQuery,
	useGetUserProfileQuery,
	useUpdateUserProfileMutation,
} from "@api/accountManagement";
import enums from "@enums/index.js";
import moment from "moment";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import CopyIcon from "../../../../assets/icons/CopyIcon.jsx";
import AccountDetailsForm from "./AccountDetailsForm/index.js";

import { showAlert } from "@components/alert/action";
import TenantIcon from "@components/tenantProfileIcon/index.js";
import { calculateTimeOnPlatform } from "@pages/profile-v2/utils/index.js";
import { copyToClipboard, setUser } from "@utils/index.js";
import { setNationalIdInMemory } from "@utils/userPiiMemory.js";
import { useSelector } from "react-redux";
import {
	ContentWrapper,
	LastLoginIp,
	LastLoginTime,
	UserId,
	UserIdTitle,
	UserIdWrapper,
	UserProfile,
	UserProfileWrapper,
	UserTime,
	Username,
} from "./elements.js";

const selectAuthUser = (state) => state.auth.user;

const UserInformation = ({ translate }) => {
	const dispatch = useDispatch();

	const authUser = useSelector(selectAuthUser);

	const { data: userInfo, refetch: refetchUserInfo } = useGetUserProfileQuery();
	const { data: lastLoginData } = useGetLastLoginQuery();
	const [updateUserProfileMutation] = useUpdateUserProfileMutation();

	const updateProfile = authUser?.updateProfile ?? false;

	useEffect(() => {
		const localStorageUser = JSON.parse(localStorage.getItem("user_data") || "{}");

		if (
			authUser &&
			authUser.username &&
			localStorageUser.updateProfile === true &&
			authUser.updateProfile !== true
		) {
			const syncedUser = {
				...authUser,
				updateProfile: localStorageUser.updateProfile,
			};

			dispatch({
				type: enums.LOGIN_SUCCESS,
				payload: syncedUser,
			});
		}
	}, [authUser, dispatch]);

	const parseUserId = (userId) => {
		let strNumber = userId?.substring(0, 14);
		let result = strNumber + "...";
		return result;
	};

	const handleSaveUserInfo = async (payload) => {
		try {
			await updateUserProfileMutation(payload).unwrap();

			const updatedUser = {
				...authUser,
				updateProfile: false,
			};

			const nationalIdAttr = payload?.UserAttributes?.find(
				(a) => a.Name === "custom:nationalId"
			);

			if (nationalIdAttr?.Value) {
				setNationalIdInMemory(nationalIdAttr.Value);
			}

			dispatch({
				type: enums.LOGIN_SUCCESS,
				payload: updatedUser,
			});

			setUser(updatedUser);

			dispatch(
				showAlert({
					type: "success",
					msg: "PROFILE_UPDATED",
					title: "SUCCESSFULL_TITLE",
				})
			);

			refetchUserInfo();
		} catch (error) {
			const errorCode = error?.data?.code || error?.error?.code;
			const profileUpdateErrorMessage =
				errorCode === "EmailLinkedToSocialIdentity"
					? "EmailLinkedToSocialIdentity"
					: "GENERIC_ERROR";

			dispatch(
				showAlert({
					type: "error",
					msg: profileUpdateErrorMessage,
					title: "ERROR",
				})
			);
		}
	};

	const usernameLetter = userInfo?.username?.charAt(0).toUpperCase();

	const formatLastLoginTime = (timestamp) => {
		if (!timestamp) return translate("Never");
		return moment.unix(timestamp).format("DD.MM.YYYY, HH:mm");
	};

	return (
		<ContentWrapper>
			<UserProfileWrapper>
				<TenantIcon
					hasLetter={window.PROFILE_ICON_HAS_LETTER}
					username={usernameLetter}
					width={90}
				/>
				<UserProfile>
					<Username>{userInfo?.username}</Username>
					<UserIdWrapper>
						<UserIdTitle>ID </UserIdTitle>
						<UserId>{parseUserId(userInfo?.id)}</UserId>
						<CopyIcon onClick={() => copyToClipboard(userInfo?.id, translate)} />
					</UserIdWrapper>
					<UserTime>{calculateTimeOnPlatform(userInfo?.create_date, translate)}</UserTime>
					{lastLoginData && (
						<>
							<LastLoginTime>
								{translate("Last Login")}:{" "}
								{formatLastLoginTime(lastLoginData.lastLoginTime)}
							</LastLoginTime>
							<LastLoginIp>
								{translate("IP Address")}: {lastLoginData.lastLoginIp}
							</LastLoginIp>
						</>
					)}
				</UserProfile>
			</UserProfileWrapper>
			<AccountDetailsForm
				key={`account-form-${updateProfile}`}
				username={userInfo?.username}
				userInfo={userInfo}
				translate={translate}
				onSave={handleSaveUserInfo}
				updateProfile={updateProfile}
			/>
		</ContentWrapper>
	);
};

export default UserInformation;
